import type { ChildRoute } from 'pages/AccountDrawerPage/types';
import type { Sources } from 'pages/ManageConnectionsPage/types';

import {
    AAM_PAGE,
    ACCOUNT_CERTIFICATIONS,
    ACCOUNT_DETAILS,
    ACCOUNT_LOGIN_PAGE,
    ACCOUNT_LOGOUT_PAGE,
    ACCOUNT_NEW_APPLICATOR_CERTIFICATION,
    ACCOUNT_PAGE,
    ACCOUNT_SETTINGS,
    ACM_PAGE,
    CREATE_CUSTOMER_PAGE,
    CREATE_FAA_REPORT_PAGE,
    CREATE_FLIGHT_GROUP_PAGE,
    CREATE_INVOICE_PAGE,
    CREATE_ORGANIZATAION_PAGE,
    CUSTOMERS_PAGE,
    DRONE_FLEET_PAGE,
    EDIT_AIRCRAFT_PAGE,
    EDIT_APPLICATOR_CERTIFICATION_PAGE,
    EDIT_CERTIFICATIONS_PAGE,
    EDIT_COMPANY_DETAILS_PAGE,
    EDIT_CONTROLLER_PAGE,
    EDIT_PERSON_OF_CONTACT_PAGE,
    EDIT_PESTICIDE_LICENSE_PAGE,
    EDIT_USER_DETAILS_PAGE,
    EQUIPMENT_PAGE,
    EQUIPMENT_ROUTE,
    EXEMPTIONS_ROUTE,
    FAA_REPORTS_PAGE,
    FLIGHT_GROUPS_PAGE,
    FLIGHT_GROUP_PAGE,
    FLIGHT_LOG_PAGE,
    GENERAL_INFO_ROUTE,
    HOME_PAGE,
    INVOICE_PAGE,
    MANAGE_CONNECTIONS_PAGE,
    MANAGE_ORGANIZATION_PAGE,
    MAPS_PAGE,
    MAP_GENERATION_PAGE,
    OPERATOR_MAP_PAGE,
    ORGANIZATION_DETAILS,
    ORGANIZATION_EDIT_MEMBER,
    ORGANIZATION_EQUIPMENT,
    ORGANIZATION_EQUIPMENT_AIRCRAFT,
    ORGANIZATION_EQUIPMENT_CONTROLLER,
    ORGANIZATION_INVITATIONS,
    ORGANIZATION_NEW_AIRCRAFT,
    ORGANIZATION_NEW_CONTROLLER,
    ORGANIZATION_NEW_INVITATION,
    ORGANIZATION_NEW_MEMBER,
    ORGANIZATION_NEW_PESTICIDE_LICENSE,
    ORGANIZATION_SUBSCRIPTION,
    ORGANIZATION_TEAM_DETAILS,
    ORGANIZATION_TEAM_DETAILS_ACTIVE,
    ORGANIZATION_TEAM_DETAILS_DISABLED,
    ORGANIZATION_TEAM_DETAILS_INVITED,
    ORGANIZATION_USERS,
    PRODUCT_USAGE_REPORT_PAGE,
    PROVIDER_MAP,
    SEND_INVOICE_PAGE,
    SUBSCRIPTION_ROUTE,
    TERMS_OF_SERVICE_PAGE,
    UPLOAD_FLIGHT_LOG,
    VERIFY_ACCOUNT_PAGE,
    VERIFY_EMAIL_PAGE,
    VERIFY_ORGANIZATION_PAGE,
    VIEW_FAA_REPORT_PAGE,
    WORK_ORDER_PAGE
} from './constants';

const usePageRoutes = () => ({
    aamPage: AAM_PAGE,
    accountCertifications: ACCOUNT_CERTIFICATIONS,
    accountDetails: ACCOUNT_DETAILS,
    accountDrawerPage: (childRoute: ChildRoute) => `${ACCOUNT_PAGE}/${childRoute}`,
    accountLoginPage: ACCOUNT_LOGIN_PAGE,
    accountLogoutPage: ACCOUNT_LOGOUT_PAGE,
    accountNewApplicatorCertification: ACCOUNT_NEW_APPLICATOR_CERTIFICATION,
    accountPage: ACCOUNT_PAGE,
    accountSettings: ACCOUNT_SETTINGS,
    acmPage: ACM_PAGE,
    asAppliedMapPage: ({ jobId }: { jobId?: string }) => `${AAM_PAGE}/${jobId}/AAM`,
    asCoveredMapPage: ({ jobId }: { jobId?: string }) => `${AAM_PAGE}/${jobId}/COVERAGE`,
    asDryCoveredMapPage: ({ jobId }: { jobId?: string }) => `${AAM_PAGE}/${jobId}/DRY_COVERAGE`,
    asFlownMapPage: ({ jobId }: { jobId?: string }) => `${AAM_PAGE}/${jobId}/AS_FLOWN`,
    createCustomerPage: CREATE_CUSTOMER_PAGE,
    createFaaReportPage: (id: string) => `${CREATE_FAA_REPORT_PAGE}/${id}`,
    createFlightGroupPage: CREATE_FLIGHT_GROUP_PAGE,
    createInvoicePage: CREATE_INVOICE_PAGE,
    createOrganizationPage: CREATE_ORGANIZATAION_PAGE,
    customersPage: CUSTOMERS_PAGE,
    droneFleetPage: DRONE_FLEET_PAGE,
    editAircraftPage: (id: string) => `${EDIT_AIRCRAFT_PAGE}/${id}`,
    editApplicatorCertificationPage: (id: string) => `${EDIT_APPLICATOR_CERTIFICATION_PAGE}/${id}`,
    editCertificationsPage: EDIT_CERTIFICATIONS_PAGE,
    editCompanyDetailsPage: EDIT_COMPANY_DETAILS_PAGE,
    editControllerPage: (id: string) => `${EDIT_CONTROLLER_PAGE}/${id}`,
    editCustomersPage: (id: string) => `${CUSTOMERS_PAGE}/${id}/edit`,
    editFlightGroupPage: (id: string) => `${FLIGHT_GROUP_PAGE}/${id}/edit`,
    editInvoicePage: (id: string) => `${CREATE_INVOICE_PAGE}/${id}/edit`,
    editOperatorMapPage: (id: string) => `${OPERATOR_MAP_PAGE}/${id}/edit`,
    editPersonOfContactPage: EDIT_PERSON_OF_CONTACT_PAGE,
    editPesticideLicensePage: (id: string) => `${EDIT_PESTICIDE_LICENSE_PAGE}/${id}`,
    editUserApplicatorCertificationPage: (userId: string, certificationId: string) =>
        `${ORGANIZATION_EDIT_MEMBER}/${userId}/applicator-certification/${certificationId}`,
    editUserDetailsPage: EDIT_USER_DETAILS_PAGE,
    equipment: EQUIPMENT_ROUTE,
    equipmentPage: EQUIPMENT_PAGE,
    exemptions: EXEMPTIONS_ROUTE,
    faaReportsPage: FAA_REPORTS_PAGE,
    flightDetailsPage: (id: string) => `${FLIGHT_LOG_PAGE}/${id}`,
    flightGroupAddFlightPage: (id: string) => `${FLIGHT_GROUP_PAGE}/${id}/flights`,
    flightGroupFlightUploadPage: (id: string) => `${FLIGHT_GROUP_PAGE}/${id}/flights/upload`,
    flightGroupPage: (id: string) => `${FLIGHT_GROUP_PAGE}/${id}`,
    flightGroupsPage: FLIGHT_GROUPS_PAGE,
    flightLogPage: FLIGHT_LOG_PAGE,
    generalInfo: GENERAL_INFO_ROUTE,
    generateMapPage: MAP_GENERATION_PAGE,
    homePage: HOME_PAGE,
    invoicePage: (id: string) => `${INVOICE_PAGE}/${id}`,
    manageConnectionsPage: (
        code?: string,
        source?: Sources,
        realmId?: string,
        oauthState?: string
    ) =>
        `${MANAGE_CONNECTIONS_PAGE}/?code=${code}&source=${source}&realmId=${realmId}&oauthState=${oauthState}`,
    manageOrganizationPage: MANAGE_ORGANIZATION_PAGE,
    mapPage: (jobId: string, mapType: string) => `${AAM_PAGE}/${jobId}/${mapType}`,
    mapsPage: MAPS_PAGE,
    newProductUsageReportPage: (workOrderId: string) =>
        `${WORK_ORDER_PAGE}/${workOrderId}/product-usage-report`,
    operatorMapPage: OPERATOR_MAP_PAGE,
    organizationDetails: ORGANIZATION_DETAILS,
    organizationEquipment: ORGANIZATION_EQUIPMENT,
    organizationEquipmentAircraft: ORGANIZATION_EQUIPMENT_AIRCRAFT,
    organizationEquipmentController: ORGANIZATION_EQUIPMENT_CONTROLLER,
    organizationInvitations: ORGANIZATION_INVITATIONS,
    organizationNewAircraft: ORGANIZATION_NEW_AIRCRAFT,
    organizationNewController: ORGANIZATION_NEW_CONTROLLER,
    organizationNewInvitation: ORGANIZATION_NEW_INVITATION,
    organizationNewPesticideLicense: ORGANIZATION_NEW_PESTICIDE_LICENSE,
    organizationNewUserApplicatorCertification: (userId: string) =>
        `${ORGANIZATION_NEW_MEMBER}/${userId}/applicator-certification`,
    organizationSubscription: ORGANIZATION_SUBSCRIPTION,
    organizationTeamDetails: ORGANIZATION_TEAM_DETAILS,
    organizationTeamDetailsActive: ORGANIZATION_TEAM_DETAILS_ACTIVE,
    organizationTeamDetailsDisabled: ORGANIZATION_TEAM_DETAILS_DISABLED,
    organizationTeamDetailsInvited: ORGANIZATION_TEAM_DETAILS_INVITED,
    organizationUser: (userId: string) => `${ORGANIZATION_USERS}/${userId}`,
    organizationUsers: ORGANIZATION_USERS,
    productUsageReportEditPage: (id: string) => `${PRODUCT_USAGE_REPORT_PAGE}/${id}/edit`,
    productUsageReportPage: (id: string) => `${PRODUCT_USAGE_REPORT_PAGE}/${id}`,
    productUsageReportPageWithModal: `${PRODUCT_USAGE_REPORT_PAGE}/newPur`,
    productUsageReportsPage: PRODUCT_USAGE_REPORT_PAGE,
    providerMapViewPage: (id: string) => `${PROVIDER_MAP}/${id}`,
    sendInvoicePage: (id: string) => `${SEND_INVOICE_PAGE}/${id}`,
    stripeSubscription: SUBSCRIPTION_ROUTE,
    termsOfServicePage: TERMS_OF_SERVICE_PAGE,
    uploadFlightLogPage: UPLOAD_FLIGHT_LOG,
    verifyAccountPage: VERIFY_ACCOUNT_PAGE,
    verifyEmailPage: VERIFY_EMAIL_PAGE,
    verifyOrganizationPage: VERIFY_ORGANIZATION_PAGE,
    viewFaaReportPage: (id: string) => `${VIEW_FAA_REPORT_PAGE}/${id}`,
    workOrderDuplicatePage: (id: string) => `${WORK_ORDER_PAGE}/${id}/duplicate`,
    workOrderEditPage: (id: string) => `${WORK_ORDER_PAGE}/${id}/edit`,
    workOrderPage: WORK_ORDER_PAGE,
    workOrderPageWithId: (id: string) => `${WORK_ORDER_PAGE}/${id}`
});

export default usePageRoutes;
